import classNames from "classnames";
import React, { FC, useCallback } from "react";
import { Calendar, useCalendar, UseCalendarProps, useIsMobile } from "../../hooks";
import { CSS_CLASSES } from "./date-time-input-css-classes";
import { TimeProps } from "./date-time-input.types";
import { CalendarToDateInputProps, DateInput, DateInputProps } from "./date/date-input";
import { TimeInput } from "./time/time-input";

export interface DateTimeOwnProps {
  id?: string;
  name?: string;
  className?: string;
  dateWrapperClassName?: string;
  timeWrapperClassName?: string;
  timePlaceholder?: string;
  onlyDate?: boolean;
  onlyTime?: boolean;
}

export type DateTimeToUseCalendarPassThroughProps = Omit<UseCalendarProps, keyof DateTimeOwnProps>;

export type DateTimeToDateInputPassThroughProps = Omit<
  DateInputProps,
  keyof DateTimeOwnProps | "onDateChange" | "withTime" | keyof CalendarToDateInputProps
>;

export type DateTimeToTimeInputPassThroughProps = Partial<
  Omit<TimeProps, keyof DateTimeOwnProps | "selectedTime" | "onTimeSelect">
>;

export type DateTimeProps = DateTimeOwnProps &
  DateTimeToUseCalendarPassThroughProps &
  DateTimeToDateInputPassThroughProps &
  DateTimeToTimeInputPassThroughProps;

export const DateTime: FC<DateTimeProps> = ({
  /* DateTime own props */
  id,
  className,
  timePlaceholder,
  name,
  onlyDate = false,
  onlyTime = false,
  dateWrapperClassName,
  timeWrapperClassName,
  /* useCalendar pass through props */
  selectedDate,
  onDateSelect,
  onInvalidDate,
  dateFormat = "MM/dd/yyyy",
  showMonths = 2,
  minDate,
  maxDate,
  excludeDates,
  includeDates,
  selectsStart = false,
  selectsEnd = false,
  isWeekEnd,
  startDate,
  endDate,
  withTime,

  /** Date pass through props */
  datePlaceholder,
  datePicker,
  maskOptions,
  required,
  disabled,
  autoComplete,
  onFocus,
  onBlur,
  dateInputWrapperClassName,
  dateInputClassName,
  dateInputBorderClassName,
  datePopoverClassName,
  datePopoverContentClassName,
  showOutsideMonthDates,
  allowSelectOutsideMonthDates,
  showDateClearButton = false,
  alwaysShowDateClearButton = false,
  placement,
  flip,
  modal,
  usePortal,
  /** Time pass through props */
  timeFormat,
  label,
  timePicker = false,
  hourPicker = false,
  minutePicker = false,
  showMinuteClearButton = false,
  showHourClearButton = false,
  showTimeClearButton = false,
  clockMode = "12-hour",
  minuteInterval,
  hourInterval,
  minuteOptions,
  hourOptions,
  timeOptions,
  timeInputClassName,
  timePickerClassName,
  hourInputClassName,
  hourPickerClassName,
  minuteInputClassName,
  minutePickerClassName,
  meridianClassName,
  /* Date and Time poss through props */
  color = "interactive",

  /* useCalendar pass through props */
  minTime,
  maxTime,
  ...rest
}) => {
  const isMobile = useIsMobile(true);
  const calendar: Calendar = useCalendar({
    selectedDate,
    onInvalidDate,
    dateFormat,
    onDateSelect,
    showMonths: isMobile ? 1 : showMonths,
    minDate,
    maxDate,
    excludeDates,
    includeDates,
    selectsStart,
    selectsEnd,
    isWeekEnd,
    startDate,
    endDate,
    withTime: !onlyDate,
    onlyTime,
    timeIntervals: minuteInterval || hourInterval,
  });

  const handleTimeSelect = useCallback(
    (time: Date | null) => {
      calendar.selectTime(time);
    },
    [calendar]
  );

  const handleDateClear = useCallback(() => {
    onDateSelect(null);
  }, [onDateSelect]);

  const renderTime = () => {
    return (
      <TimeInput
        id={onlyTime ? id : `t-${id}`}
        className={timeWrapperClassName}
        selectedTime={selectedDate as Date}
        onTimeSelect={handleTimeSelect}
        timePlaceholder={timePlaceholder}
        timeFormat={timeFormat}
        name={name}
        label={label}
        required={required}
        disabled={disabled}
        autoComplete={autoComplete}
        onFocus={onFocus}
        onBlur={onBlur}
        timePicker={timePicker}
        hourPicker={hourPicker}
        minutePicker={minutePicker}
        clockMode={clockMode}
        minuteInterval={minuteInterval}
        hourInterval={hourInterval}
        minuteOptions={minuteOptions}
        hourOptions={hourOptions}
        timeOptions={timeOptions}
        minTime={minTime}
        maxTime={maxTime}
        times={calendar.times}
        showMinuteClearButton={showMinuteClearButton}
        showHourClearButton={showHourClearButton}
        showTimeClearButton={showTimeClearButton}
        timeInputClassName={timeInputClassName}
        timePickerClassName={timePickerClassName}
        hourInputClassName={hourInputClassName}
        hourPickerClassName={hourPickerClassName}
        minuteInputClassName={minuteInputClassName}
        minutePickerClassName={minutePickerClassName}
        meridianClassName={meridianClassName}
        usePortal={usePortal}
        color={color}
      />
    );
  };

  const dateTimeWrapperClass = classNames(CSS_CLASSES.DATE_TIME_INPUT_WRAPPER, className);
  return (
    <div className={dateTimeWrapperClass} {...rest}>
      {!onlyTime && (
        <DateInput
          {...calendar}
          onFocus={onFocus}
          onBlur={onBlur}
          className={dateWrapperClassName}
          id={id}
          name={name}
          datePicker={datePicker}
          maskOptions={maskOptions}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          datePlaceholder={datePlaceholder}
          withTime={!onlyDate}
          selectsStart={selectsStart}
          selectsEnd={selectsEnd}
          dateInputWrapperClassName={dateInputWrapperClassName}
          dateInputClassName={dateInputClassName}
          dateInputBorderClassName={dateInputBorderClassName}
          datePopoverClassName={datePopoverClassName}
          datePopoverContentClassName={datePopoverContentClassName}
          showOutsideMonthDates={showOutsideMonthDates === undefined && isMobile ? true : showOutsideMonthDates}
          allowSelectOutsideMonthDates={allowSelectOutsideMonthDates}
          onDateClear={handleDateClear}
          showDateClearButton={showDateClearButton}
          alwaysShowDateClearButton={alwaysShowDateClearButton}
          placement={placement}
          flip={flip}
          modal={modal}
          usePortal={usePortal}
          color={color}
        />
      )}
      {!onlyDate && renderTime()}
    </div>
  );
};
